import { useEffect, useState, useSyncExternalStore } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { useCaptchaHostStoreContext } from '#src/app/shared/hooks/perimeterX';

import { PerimeterXCaptchaModal } from './PerimeterXCaptchaModal';

/**
 * Whether a React Aria overlay is still playing its enter animation (`data-entering`). A sheet
 * waits until it has entered before it makes the rest of the page inert, and that pass also catches
 * an overlay that mounted in the meantime, with no way back. The compose sheets of the contact-DM
 * flow trip this: a challenge that lands within the drafting sheet's slide-in left the captcha inert.
 */
function useIsOverlayEntering(): boolean {
	return useSyncExternalStore(
		(onStoreChange) => {
			const observer = new MutationObserver(onStoreChange);
			observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['data-entering'] });
			return () => observer.disconnect();
		},
		() => document.querySelector('[data-entering]') !== null,
		() => false,
	);
}

/**
 * Global host for the imperative PerimeterX captcha (see
 * docs/architecture/decisions/2026-06-18-perimeterx-captcha-global-host.md).
 * Mounted once in the app, it renders the captcha modal whenever a challenge
 * is opened via usePerimeterXCaptcha(), and renders null while idle.
 */

export const PerimeterXCaptchaHost: React.FC<EmptyProps> = () => {
	const captchaHostStore = useCaptchaHostStoreContext();
	const state = useSyncExternalStore(
		(onStoreChange) => captchaHostStore.subscribe(onStoreChange),
		() => captchaHostStore.getSnapshot(),
		() => captchaHostStore.getServerSnapshot(),
	);
	const location = useLocation();

	// settle any pending challenge on unmount so no awaiting caller can hang
	useEffect(() => {
		return () => {
			captchaHostStore.dismiss();
		};
	}, []);

	// dismiss a pending challenge on navigation, since this host outlives route changes;
	// depend on the location's own string fields rather than the location object itself,
	// since its identity isn't stable across re-renders unrelated to navigation (e.g. this
	// component's own state changes), which would otherwise dismiss a challenge right as it opens
	const { pathname, search, hash } = location;
	useEffect(() => {
		captchaHostStore.dismiss();
	}, [pathname, search, hash]);

	// Open only once no other overlay is entering. The captcha's own enter animation then must not
	// hide it again, so a challenge stays shown once it has been shown.
	const isOverlayEntering = useIsOverlayEntering();
	const pendingChallengeId = state.status === 'pending' ? state.challengeId : undefined;
	const [shownChallengeId, setShownChallengeId] = useState<number>();
	const isShown = pendingChallengeId !== undefined && (pendingChallengeId === shownChallengeId || !isOverlayEntering);
	useEffect(() => {
		if (isShown) setShownChallengeId(pendingChallengeId);
	}, [isShown, pendingChallengeId]);

	if (state.status !== 'pending' || !isShown) return null;

	return (
		<PerimeterXCaptchaModal
			key={state.challengeId}
			isOpen
			setIsOpen={(isOpen) => {
				if (!isOpen) captchaHostStore.dismiss();
			}}
			pxResponse={state.pxResponse}
			onResult={(result) => {
				captchaHostStore.resolve(result);
			}}
		/>
	);
};
